import * as React from 'react'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { Typography } from '@mui/material'
import { CalendarToday } from '@mui/icons-material'
import dayjs from 'dayjs'
import { FieldErrors } from 'react-hook-form'
import theme from '../theme'

interface Props {
  onChange: (e: dayjs.Dayjs | null) => void
  value: dayjs.Dayjs | null
  label: string
  minDate?: dayjs.Dayjs
  maxDate?: dayjs.Dayjs
  disabled?: boolean
  width?: string
  errors?: FieldErrors
  name?: string
  placeholder?: string
  style?: React.CSSProperties
  minDateTime?: dayjs.Dayjs
  maxDateTime?: dayjs.Dayjs
}

const DatePicker = React.forwardRef<HTMLInputElement, Props>(
  (
    {
      onChange,
      value,
      label,
      minDate,
      maxDate,
      disabled,
      width = '200px',
      errors,
      name,
      placeholder = 'MM/DD/YYYY',
      style,
      minDateTime,
      maxDateTime,
    },
    ref
  ) => {
    const [draft, setDraft] = React.useState<dayjs.Dayjs | null>(value)

    // Sync draft state when value prop changes (e.g., when loading existing flight data)
    React.useEffect(() => {
      setDraft(value)
    }, [value])

    const commit = (v: dayjs.Dayjs | null) => {
      if (v && !v.isValid()) return
      onChange(v)
    }

    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Typography variant="body2">{label}</Typography>

        <DateTimePicker
          sx={{ width, ...style, paddingBottom: theme.space['4'] }}
          slots={{ openPickerIcon: CalendarToday }}
          slotProps={{
            textField: {
              inputRef: ref,
              placeholder,
              onBlur: () => commit(draft),
              error: Boolean(name && errors?.[name]),
              helperText: name ? (errors?.[name]?.message as string) : '',
              onKeyDown: (e) => {
                if (e.key === 'Enter') commit(draft)
              },
            },
          }}
          format="MM/DD/YYYY"
          ampm
          value={draft}
          onChange={(newValue) => setDraft(newValue)}
          onAccept={(newValue) => commit(newValue)}
          maxDate={maxDate}
          minDate={minDate}
          disabled={disabled}
          minDateTime={minDateTime}
          maxDateTime={maxDateTime}
          views={['year', 'month', 'day']}
        />
      </LocalizationProvider>
    )
  }
)

DatePicker.displayName = 'DatePicker'
export default DatePicker
